import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  getPublicOffering,
  simulate,
  type OfferingDetail,
} from '../../lib/offerings';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort, formatMoney, formatMoneyCompact } from '../../lib/format';
import { OfferingLegends, ForwardLookingNote } from '../../components/OfferingDisclosures';
import { ApplyToInvest } from '../../components/ApplyToInvest';
import { offeringWithFinancials } from '../../lib/offerings';
import { SimulationWatermark } from '../../components/SimulationWatermark';
import './OfferingDetailPage.css';

const SECURITY_LABEL: Record<string, string> = {
  equity_units: 'Equity units',
  revenue_share: 'Revenue share',
  convertible_note: 'Convertible note',
  safe: 'SAFE',
};

/**
 * The subtraction this simulator cannot make, restated.
 *
 * THIS USED TO SAY SOMETHING THAT IS NO LONGER TRUE. It warned that every figure was
 * optimistic "by the full production budget", because settlement deducted documented
 * production costs from revenue before distributing anything. That deduction was itself
 * the error: the raise IS the funded production budget, so taking it out of revenue as
 * well charged the same cost twice and made investors buy the production back out of
 * their own return. Return of capital is where the budget is accounted for.
 *
 * What the projection still cannot see is expenses the raise did NOT pay: settlement
 * costs, taxes, post-event expenses. Those are real and not public, so the figures remain
 * an upper bound, just by a much smaller and differently-shaped amount.
 *
 * Saying so is not optional politeness. A projection that silently omits a subtraction is
 * a misleading statement about returns, and the omission is invisible to a reader who
 * does not already know the waterfall.
 */
function ProductionCostCaveat() {
  return (
    <p className="sim__caveat" role="note">
      <strong>Some expenses are not included.</strong> The capital raised funds the
      production itself, and is returned first at settlement rather than deducted from
      ticket revenue. Expenses the raise does not cover, such as settlement costs, taxes
      and post-event expenses, are not public and are not shown here, so every figure is
      higher than an actual distribution would be.
    </p>
  );
}

/**
 * Where the sell-through slider starts.
 *
 * NOT the live figure, and deliberately so. publicOfferings.js does not select
 * quantity_sold or shares_sold for this endpoint: live sales are performance of the
 * offering rather than a term of it, and publishing them to an anonymous visitor is a
 * disclosure decision that was made the other way. There is therefore no live baseline
 * to diverge FROM here, so divergence is measured against this openly arbitrary midpoint.
 */
const BASELINE_SELL_THROUGH = 50;

export function OfferingDetailPage() {
  const { offeringId } = useParams<{ offeringId: string }>();

  const [offering, setOffering] = useState<OfferingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Simulator inputs. Sell-through starts at half the house — a neutral default
  // rather than a sold-out one, which would open on the rosiest possible number.
  const [sellThrough, setSellThrough] = useState(BASELINE_SELL_THROUGH);
  const [shares, setShares] = useState<number | null>(null);

  // Set once a control leaves its baseline and never cleared by returning to it: the
  // screenshot taken at 95% sell-through still exists after the slider is dragged back.
  const [isSimulating, setIsSimulating] = useState(false);

  useEffect(() => {
    if (!offeringId) return;
    let cancelled = false;
    setLoading(true);
    getPublicOffering(offeringId)
      .then((result) => {
        if (cancelled) return;
        setOffering(result);
        // Only meaningful when figures were sent. An unapproved viewer has no minimum to
        // seed from, and no simulator to seed it for.
        if (result.structure) setShares(result.structure.minimumShares);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A 506(b) deal and a deal that does not exist both land here, and they
        // are indistinguishable by design — the engine returns the same 404.
        if (err instanceof ApiError && err.status === 404) setNotFound(true);
        else setError(err instanceof ApiError ? err.message : 'Could not load this offering.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [offeringId]);

  /**
   * The gate, narrowed once for the whole component.
   *
   * Null whenever the engine withheld figures, which is every unapproved or anonymous
   * viewer. Everything financial below reads from `gated`, never from `offering`, so the
   * compiler refuses to let a price reach the page on the ungated path.
   */
  const gated = useMemo(() => offeringWithFinancials(offering), [offering]);

  const projection = useMemo(() => {
    if (!gated || shares == null) return null;
    const soldByTier = Object.fromEntries(
      gated.ticketTiers.map((tier) => [
        tier.name,
        Math.round((tier.quantityTotal * sellThrough) / 100),
      ]),
    );
    return simulate(gated, { soldByTier, shares });
  }, [gated, sellThrough, shares]);

  // Derived from the rates the ENGINE published for this offering, never written as
  // literals in the label. A hardcoded "80%" beside a figure computed at 60% is the exact
  // failure this screen must not have: a prospective investor reading a split that the
  // settlement will not honour.
  const investorPct = Math.round((gated?.simulator.rates.investorSplitRate ?? 0) * 100);
  const gpPct = 100 - investorPct;

  if (loading) return <p className="offering-state">Loading offering…</p>;

  if (notFound) {
    return (
      <div className="offering-state">
        <h1>Offering not available</h1>
        <p>
          This offering isn’t open to the public. It may have closed, or it may not be
          publicly listed.
        </p>
        <Link to="/invest" className="button button--primary">
          Browse open offerings
        </Link>
      </div>
    );
  }

  if (error || !offering) {
    return (
      <div className="alert alert--error" role="alert">
        {error ?? 'Could not load this offering.'}
      </div>
    );
  }

  const where = [offering.event.venueName, offering.event.venueCity].filter(Boolean).join(' · ');

  /**
   * WHAT AN UNAPPROVED OR ANONYMOUS VISITOR SEES.
   *
   * That the offering exists, what it is called, which show it backs, its description,
   * and the mandatory legends. No price, no minimum, no target, no amount raised, no
   * percent funded, and no projection of any kind, because the engine did not send any
   * of it. This branch renders what arrived; it is not hiding figures that are sitting
   * in the payload.
   *
   * The Apply to Invest path stays open, since the whole point of a 506(c) teaser is to
   * let someone start the accreditation process from it.
   */
  if (!gated) {
    return (
      <div className="offering-page offering-page--narrow" data-surface="invest">
        <nav className="offering-crumbs">
          <Link to="/invest">← All offerings</Link>
        </nav>

        <header className="offering-hero">
          <p className="offering-hero__eyebrow mono">
            Reg D · Rule 506(c) · {SECURITY_LABEL[offering.securityType] ?? offering.securityType}
          </p>
          <h1 className="offering-hero__title display">{offering.title}</h1>
          <p className="offering-hero__event">
            {offering.event.title}
            {where ? ` · ${where}` : ''} · {formatDateShort(offering.event.eventDate)}
          </p>
          {offering.description && (
            <p className="offering-hero__pitch">{offering.description}</p>
          )}
        </header>

        {/* Prominent, above everything else on the page, and not dismissible. */}
        <section className="offering-gate glass-panel glass-edge" aria-labelledby="gate-heading">
          <h2 id="gate-heading" className="offering-gate__title">
            Accredited investors only
          </h2>
          <p className="offering-gate__body">
            The terms of this offering, including price, minimum investment and any
            projected return, are shown only to investors Voleto has verified as
            accredited. Nothing financial about this offering is published publicly.
          </p>
          <p className="offering-gate__body">
            If you are an accredited investor, apply below and an administrator will
            review your verification.
          </p>
          <ApplyToInvest offeringId={offering.id} offeringTitle={offering.title} />
        </section>

        <OfferingLegends disclosures={offering.disclosures} />
      </div>
    );
  }

  const capacity = gated.ticketTiers.reduce((n, tier) => n + tier.quantityTotal, 0);

  return (
    <div className="offering-page" data-surface="invest">
      <nav className="offering-crumbs">
        <Link to="/invest">← All offerings</Link>
      </nav>

      <header className="offering-hero">
        <p className="offering-hero__eyebrow mono">
          Reg D · Rule 506(c) · {SECURITY_LABEL[offering.securityType] ?? offering.securityType}
        </p>
        <h1 className="offering-hero__title display">{offering.title}</h1>
        <p className="offering-hero__event">
          {offering.event.title}
          {where ? ` · ${where}` : ''} · {formatDateShort(offering.event.eventDate)}
        </p>
        {offering.description && (
          <p className="offering-hero__pitch">{offering.description}</p>
        )}
      </header>

      {/* Deal terms. Size of the raise and the minimum ticket, never how much has
          been taken, which the engine does not send. */}
      <section className="offering-terms glass-panel glass-edge" aria-label="Offering terms">
        <div className="offering-term">
          <span className="offering-term__label">Target raise</span>
          <span className="offering-term__value mono">
            {formatMoneyCompact(gated.targetRaiseCents)}
          </span>
        </div>
        <div className="offering-term">
          <span className="offering-term__label">Minimum investment</span>
          <span className="offering-term__value mono">
            {formatMoneyCompact(gated.minimumInvestmentCents)}
          </span>
        </div>
        <div className="offering-term">
          <span className="offering-term__label">Price per unit</span>
          <span className="offering-term__value mono">
            {formatMoney(gated.structure.pricePerShareCents)}
          </span>
        </div>
        <div className="offering-term">
          <span className="offering-term__label">Closes</span>
          <span className="offering-term__value mono">
            {offering.closesAt ? formatDateShort(offering.closesAt) : 'TBA'}
          </span>
        </div>
      </section>

      <div className="offering-columns">
        <div className="offering-main">
          {/* ── Simulator ────────────────────────────────────────────────── */}
          <section className="sim sim-anchor glass-panel" aria-labelledby="sim-heading">
            <SimulationWatermark active={isSimulating} />
            <h2 id="sim-heading" className="sim__heading">
              Model a position
            </h2>
            <p className="sim__intro">
              Move the sliders to see how this deal’s structure behaves at different levels
              of ticket sales. These are hypotheticals you choose, not forecasts by Voleto.
            </p>

            <div className="sim__controls">
              <div className="sim__control">
                <label className="field__label" htmlFor="sim-sell">
                  Hypothetical sell-through | {sellThrough}% of {capacity.toLocaleString()} seats
                  {/* Capacity is a real term from the database. Tickets SOLD is not
                      published on this endpoint, so no live sales figure is claimed. */}
                  <span className="sim-live-anchor">
                    Live: {capacity.toLocaleString()} seats on sale
                  </span>
                </label>
                <input
                  id="sim-sell"
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={sellThrough}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    setSellThrough(next);
                    if (next !== BASELINE_SELL_THROUGH) setIsSimulating(true);
                  }}
                />
              </div>

              <div className="sim__control">
                <label className="field__label" htmlFor="sim-shares">
                  Units held | minimum {gated.structure.minimumShares}
                  <span className="sim-live-anchor">
                    Live: {gated.structure.totalShares.toLocaleString()} units at{' '}
                    {formatMoney(gated.structure.pricePerShareCents)}
                  </span>
                </label>
                <input
                  id="sim-shares"
                  className="field__input mono"
                  type="number"
                  min={gated.structure.minimumShares}
                  max={gated.structure.totalShares}
                  value={shares ?? gated.structure.minimumShares}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    setShares(next);
                    if (next !== gated.structure.minimumShares) setIsSimulating(true);
                  }}
                />
              </div>
            </div>

            {/* Ticket tiers, face value and house capacity, the structural inputs. */}
            <table className="sim__tiers">
              <caption className="sim__tiers-caption">Ticket structure</caption>
              <thead>
                <tr>
                  <th scope="col">Tier</th>
                  <th scope="col">Price</th>
                  <th scope="col">Capacity</th>
                  <th scope="col">Sold at {sellThrough}%</th>
                </tr>
              </thead>
              <tbody>
                {gated.ticketTiers.map((tier) => (
                  <tr key={tier.name}>
                    <td>{tier.name}</td>
                    <td className="mono">{formatMoneyCompact(tier.priceCents)}</td>
                    <td className="mono">{tier.quantityTotal.toLocaleString()}</td>
                    <td className="mono">
                      {Math.round((tier.quantityTotal * sellThrough) / 100).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {projection && (
              <>
                <dl className="sim__waterfall">
                  <div className="sim__row">
                    <dt>Gross ticket revenue</dt>
                    <dd className="mono">{formatMoney(projection.grossTicketRevenueCents)}</dd>
                  </div>
                  <div className="sim__row sim__row--minus">
                    <dt>Voleto platform fee</dt>
                    <dd className="mono">−{formatMoney(projection.voletoPlatformFeeCents)}</dd>
                  </div>
                  <div className="sim__row sim__row--minus">
                    <dt>Card processing (2.9% + $0.30 per order)</dt>
                    <dd className="mono">−{formatMoney(projection.stripeProcessingFeeCents)}</dd>
                  </div>
                  {/* Shown ONLY where this event carries a venue arrangement, which nearly
                      none do. A permanent zero row would imply a deduction that does not
                      exist; omitting a real one would overstate the projection by exactly
                      the venue's cut, on the screen a visitor decides from. */}
                  {projection.venueRevenueShareCents > 0 && (
                    <div className="sim__row sim__row--minus">
                      <dt>
                        Venue revenue share (
                        {(gated.simulator.rates.venueRevenueShareRate * 100).toFixed(
                          gated.simulator.rates.venueRevenueShareRate * 100 % 1 ? 2 : 0)}%)
                      </dt>
                      <dd className="mono">−{formatMoney(projection.venueRevenueShareCents)}</dd>
                    </div>
                  )}
                  <div className="sim__row sim__row--total">
                    <dt>Net proceeds to the production</dt>
                    <dd className="mono">{formatMoney(projection.netProceedsCents)}</dd>
                  </div>
                  {/* The waterfall, in the order it is paid. Capital comes back before
                      anyone takes a profit share, and the promote is last. */}
                  <div className="sim__row">
                    <dt>1. Return of capital</dt>
                    <dd className="mono">{formatMoney(projection.returnOfCapitalCents)}</dd>
                  </div>
                  <div className="sim__row">
                    <dt>
                      2. Preferred return
                      {gated.structure.preferredReturnPercentage
                        ? ` (${gated.structure.preferredReturnPercentage}%)`
                        : ''}
                    </dt>
                    <dd className="mono">{formatMoney(projection.preferredReturnCents)}</dd>
                  </div>
                  <div className="sim__row">
                    <dt>3. Profit above the hurdle</dt>
                    <dd className="mono">{formatMoney(projection.profitAboveHurdleCents)}</dd>
                  </div>
                  <div className="sim__row">
                    <dt>Investor pool ({investorPct}% of the above)</dt>
                    <dd className="mono">{formatMoney(projection.investorPoolCents)}</dd>
                  </div>
                  <div className="sim__row sim__row--minus">
                    <dt>GP carried interest ({gpPct}%)</dt>
                    <dd className="mono">−{formatMoney(projection.gpFeeCents)}</dd>
                  </div>
                </dl>

                <p className="sim__note">
                  Production costs are deducted inside the production entity before this
                  waterfall runs and are not public, so every figure below net proceeds is
                  an upper bound rather than a forecast. Card processing assumes one order
                  per ticket, the most transactions a sell-through can produce, and the
                  waterfall assumes the raise is fully subscribed.
                </p>

                <div className="sim__result glass-card">
                  <div className="sim__result-block">
                    <span className="sim__result-label">You would put in</span>
                    <span className="sim__result-value mono">
                      {formatMoney(projection.investedCents)}
                    </span>
                  </div>
                  <div className="sim__result-block sim__result-block--headline">
                    {/* Labels come from the engine so no unconditional wording
                        ("will return", "guaranteed") can be introduced here. */}
                    <span className="sim__result-label">
                      {gated.simulator.resultLabel} position
                    </span>
                    <span
                      className={
                        projection.gainCents < 0
                          ? 'sim__result-value mono is-negative'
                          : 'sim__result-value mono'
                      }
                    >
                      {formatMoney(projection.netPositionCents)}
                    </span>
                    <span className="sim__result-qualifier">{gated.simulator.qualifier}</span>
                  </div>
                </div>

                {projection.gainCents < 0 && (
                  <p className="sim__loss" role="note">
                    At this level of sales the position is worth{' '}
                    <strong>{formatMoney(Math.abs(projection.gainCents))} less</strong> than
                    the amount invested. Losing part or all of an investment is a normal
                    outcome for this kind of offering.
                  </p>
                )}

                <ProductionCostCaveat />
                <ForwardLookingNote text={gated.simulator.disclaimer} />
              </>
            )}
          </section>

          {offering.lineup.length > 0 && (
            <section className="offering-lineup">
              <h2>Confirmed lineup</h2>
              <ul>
                {offering.lineup.map((artist) => (
                  <li key={artist.stageName}>
                    {artist.stageName}
                    {artist.genre ? <span className="offering-lineup__genre"> · {artist.genre}</span> : null}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <aside className="offering-aside">
          <ApplyToInvest offeringId={offering.id} offeringTitle={offering.title} />
        </aside>
      </div>

      <OfferingLegends disclosures={offering.disclosures} />
    </div>
  );
}

/** The general "register interest" page, reached when no deal is named. */
export function RegisterInterestPage() {
  return (
    <div className="offering-page offering-page--narrow" data-surface="invest">
      <nav className="offering-crumbs">
        <Link to="/invest">← All offerings</Link>
      </nav>
      <header className="offering-hero">
        <h1 className="offering-hero__title display">Register interest</h1>
        <p className="offering-hero__pitch">
          Tell us how to reach you and we’ll be in touch when there’s an offering that
          fits.
        </p>
      </header>
      <ApplyToInvest />
    </div>
  );
}
